// One row of a computer's "Values & secrets" on the Computers screen: its PC sign-in, a way in's login, a KVM box's
// login, or a value of its own. Edited in place like every row there: click it (or ✎, or Enter on the row), Enter
// saves, Esc cancels. A secret shows as dots; 👁 shows it for 30 s and ⧉ copies it, each fetched on its own and
// written to the audit (the list never carries a secret's value). × removes after 5 s, until then Undo keeps it: the
// browser never holds a secret's value, so it cannot put one back once it is gone.
import { useEffect, useRef, useState, type KeyboardEvent } from "react";

export const SHOW_S = 30;
export const UNDO_S = 5;

export type ValueSpec = {
  key: string;                 // the row's key on the screen
  label: string;               // in words: "Windows PIN"
  name?: string;               // the name it is stored and typed by (win_pin, vpn_host), shown small
  usedBy: string;              // what uses it: "unlocks the screen", "typed by the agent"
  secret: boolean;
  set: boolean;                // a value is stored
  plain?: string | null;       // a plain value's text
  user?: string | null;        // a login's user name, shown before its password (a KVM box's)
  own?: boolean;               // the computer's own value: its name is edited too, and secret or plain switched
  canEdit: boolean;
  canShow: boolean;            // a secret's value may be fetched (the managers of what it belongs to)
  readOnlyNote?: string;       // why it cannot be edited here, in place of its value
  fetch?: (why: "show" | "copy") => Promise<string>;
  save?: (v: { name: string; value: string; secret: boolean; user: string }) => Promise<unknown>;
  remove?: () => Promise<unknown>;
  removeLabel?: string;        // "Remove" for its own, "Forget" for a fixed one
};

export function ValueCells({ spec, editing, onEdit, onDone, saved }: {
  spec: ValueSpec; editing: boolean; onEdit: (on: boolean) => void; onDone: (msg: string) => void;
  saved?: string | null;       // "Saved ✓", "Copied": said on the row for a moment
}) {
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [secret, setSecret] = useState(true);
  const [user, setUser] = useState("");
  const [shown, setShown] = useState<string | null>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [gone, setGone] = useState(false);             // removed, Undo still possible
  const hide = useRef<number>(0);
  const removal = useRef<number>(0);
  useEffect(() => () => { clearTimeout(hide.current); }, []);
  useEffect(() => {
    if (!editing) return;
    setName(spec.name ?? ""); setValue(spec.secret ? "" : spec.plain ?? ""); setSecret(spec.secret);
    setUser(spec.user ?? ""); setErr("");
  }, [editing]);                                        // from the row as it is when editing starts

  const isNew = !!spec.own && !spec.name;
  const fail = (e: unknown) => setErr(String((e as Error).message));

  const show = async () => {
    if (shown !== null) { setShown(null); clearTimeout(hide.current); return; }
    try {
      setShown(await spec.fetch!("show")); setErr("");
      clearTimeout(hide.current);
      hide.current = window.setTimeout(() => setShown(null), SHOW_S * 1000);
    } catch (e) { fail(e); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(await spec.fetch!("copy")); setErr(""); onDone("Copied"); }
    catch (e) { fail(e); }
  };
  const save = async () => {
    if (busy) return;
    if (spec.own && !name.trim()) return setErr("It needs a name.");
    if ((isNew || !spec.secret && !secret) && !value) return setErr("It needs a value.");
    if (!spec.own && !value && !(spec.user != null && user !== spec.user)) return onEdit(false);  // nothing changed
    setBusy(true);
    try {
      await spec.save!({ name: name.trim(), value, secret, user: user.trim() });
      setShown(null); onEdit(false); onDone("Saved ✓");
    } catch (e) { fail(e); } finally { setBusy(false); }
  };
  const remove = () => {
    setGone(true); setErr("");
    removal.current = window.setTimeout(() => {
      spec.remove!().then(() => onDone("Removed")).catch((e) => { setGone(false); fail(e); });
    }, UNDO_S * 1000);
  };
  const undo = () => { clearTimeout(removal.current); setGone(false); };
  const keys = (e: KeyboardEvent) => {
    if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); void save(); }
    else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onEdit(false); setErr(""); }
  };

  if (editing) return <>
    <div role="gridcell" className="cc-name">
      {spec.own
        ? <input className="field cv-in" aria-label="Name" autoFocus={isNew} value={name} placeholder="name, e.g. vpn_host"
            onChange={(e) => setName(e.target.value.toLowerCase().replace(/\s+/g, "_"))} onKeyDown={keys} />
        : <span className="cv-label">{spec.label}</span>}</div>
    <div role="gridcell" className="cv-edit">
      {spec.user != null && <input className="field cv-in" aria-label="User" value={user} placeholder="user"
        onChange={(e) => setUser(e.target.value)} onKeyDown={keys} />}
      <input className="field cv-in mono" aria-label={`${spec.label || "Value"}`} autoFocus={!isNew}
        type={secret ? "password" : "text"} autoComplete="new-password" value={value}
        placeholder={secret ? (spec.set ? "type a new one, or leave empty to keep it" : "the value") : "the value"}
        onChange={(e) => setValue(e.target.value)} onKeyDown={keys} /></div>
    <div role="gridcell" className="cc-sign">
      {spec.own && <button type="button" className="btn quiet cv-kind" aria-pressed={secret}
        title={secret ? "Secret: the agent types it by name and never sees it" : "Plain: the agent reads it in computer_info"}
        onClick={() => setSecret(!secret)}>{secret ? "🔒 secret" : "🔓 plain"}</button>}
      {err && <span className="cv-err" role="alert">{err}</span>}</div>
    <div role="gridcell" className="cc-acts">
      <button type="button" className="btn quiet" onClick={() => { onEdit(false); setErr(""); }}>Cancel</button>
      <button type="button" className="btn primary" disabled={busy} onClick={() => void save()}>{busy ? "Saving…" : "Save"}</button>
    </div>
  </>;

  const shownText = spec.secret ? (shown ?? (spec.set ? "••••••" : "")) : spec.plain ?? "";
  return <>
    <div role="gridcell" className="cc-name">
      <span className={`cv-label ${gone ? "cv-gone" : ""}`}>{spec.label}</span>
      {spec.name && spec.name !== spec.label && <span className="ctag mono">{spec.name}</span>}</div>
    <div role="gridcell" className={`cv-val mono ${gone ? "cv-gone" : ""}`}
      onClick={spec.canEdit && !gone ? () => onEdit(true) : undefined}
      title={spec.canEdit ? "Click to change" : undefined}>
      {spec.user != null && <span className="cv-user">{spec.user || "no user"}</span>}
      {spec.readOnlyNote ? <span className="cmuted">{spec.readOnlyNote}</span>
        : shownText ? <span className={shown !== null ? "cv-shown" : ""}>{shownText}</span>
        : <span className="cmuted">not set</span>}</div>
    <div role="gridcell" className="cc-sign">{err ? <span className="cv-err" role="alert">{err}</span> : spec.usedBy}</div>
    <div role="gridcell" className="cc-acts">
      {saved && !gone && <span className="csaved" role="status">{saved}</span>}
      {gone
        ? <><span className="cmuted cv-note">Removed</span><button className="btn" onClick={undo}>Undo</button></>
        : <>
          {spec.secret && spec.set && spec.canShow && <>
            <button className="btn quiet" aria-label={shown !== null ? `Hide ${spec.label}` : `Show ${spec.label}`}
              title={shown !== null ? "Hide" : `Show for ${SHOW_S} s`} onClick={() => void show()}>{shown !== null ? "Hide" : "👁"}</button>
            <button className="btn quiet" aria-label={`Copy ${spec.label}`} title="Copy" onClick={() => void copy()}>⧉</button>
          </>}
          {spec.canEdit && <button className="btn quiet hov" aria-label={`Change ${spec.label}`} title="Change"
            onClick={() => onEdit(true)}>✎</button>}
          {spec.canEdit && spec.remove && spec.set && <button className="btn quiet hov danger"
            aria-label={`${spec.removeLabel ?? "Remove"} ${spec.label}`} title={spec.removeLabel ?? "Remove"} onClick={remove}>×</button>}
        </>}
    </div>
  </>;
}
